import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { apiError } from '@/lib/apiError';
import { getLedgerVouchers, tradeSetup } from '@/api/accountsBooks';
import { VoucherView } from '../books/VoucherRegisterPage';
import type { ExportCell } from '../exportReport';
import { amt, drcr, fyStartOf, num, td, tdate, th, today, useExcel, useKeyNav, usePeriod } from './reportUtils';

/* Shared components of the account reports, laid out as TallyPrime's screens. */

const quick = (): [string, string, string][] => {
  const t = today(), fy = fyStartOf(t), m = Number(t.slice(5, 7));
  // Quarters of the financial year: Apr–Jun, Jul–Sep, Oct–Dec, Jan–Mar (always in today's calendar year).
  const qStart = `${t.slice(0, 4)}-${String(m >= 10 ? 10 : m >= 7 ? 7 : m >= 4 ? 4 : 1).padStart(2, '0')}-01`;
  return [['This year', fy, t], ['This quarter', qStart, t], ['This month', `${t.slice(0, 8)}01`, t]];
};

/** Period bar: From / To (or As on), quick periods, the page's options, Excel and Print. */
export function PeriodBar({ asOn, onBack, children, onExcel, noPeriod }: {
  asOn?: boolean; onBack?: () => void; children?: ReactNode; onExcel?: () => void; noPeriod?: boolean;
}) {
  const [p, setP] = usePeriod();
  const t = today();
  return (
    <div className="print:hidden">
      <FilterBar>
        {onBack && <div className="col-span-3 sm:col-auto"><Button variant="glass" size="sm" onClick={onBack} title="Esc">← Back</Button></div>}
        {!noPeriod && (asOn ? (
          <FilterField label="As on"><Input type="date" max={t} value={p.to} onChange={(e) => e.target.value && setP({ from: p.from > e.target.value ? e.target.value : p.from, to: e.target.value })} /></FilterField>
        ) : (
          <>
            <FilterField label="From"><Input type="date" max={t} value={p.from} onChange={(e) => e.target.value && setP({ from: e.target.value, to: p.to < e.target.value ? e.target.value : p.to })} /></FilterField>
            <FilterField label="To"><Input type="date" max={t} value={p.to} onChange={(e) => e.target.value && setP({ to: e.target.value, from: p.from > e.target.value ? e.target.value : p.from })} /></FilterField>
            <div className="col-span-3 flex gap-1 sm:col-auto sm:self-end">
              {quick().map(([l, f, to]) => (
                <button key={l} type="button" onClick={() => setP({ from: f, to })}
                  className={clsx('h-7 rounded-md border px-2 text-[11px] font-semibold sm:h-8', p.from === f && p.to === to ? 'border-accent bg-accent text-white' : 'border-[var(--border-color)] text-[var(--text-secondary)]')}>
                  {l}
                </button>
              ))}
            </div>
          </>
        ))}
        {children}
        <div className="col-span-3 flex gap-1 sm:col-auto sm:ml-auto sm:self-end">
          {onExcel && <Button variant="glass" size="sm" onClick={onExcel}>Excel</Button>}
          <Button variant="glass" size="sm" onClick={() => window.print()}>Print / PDF</Button>
        </div>
      </FilterBar>
    </div>
  );
}

export function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="col-span-3 flex items-center gap-1.5 text-xs text-[var(--text-secondary)] sm:col-auto sm:self-end sm:pb-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

export function Seg<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="col-span-3 flex gap-1 sm:col-auto sm:self-end">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={clsx('h-7 rounded-md border px-2 text-[11px] font-semibold sm:h-8', value === v ? 'border-accent bg-accent text-white' : 'border-[var(--border-color)] text-[var(--text-secondary)]')}>
          {l}
        </button>
      ))}
    </div>
  );
}

/** Tally's title strip — report · company · period — over the report's table(s). */
export function ReportFrame({ title, right, children, note }: { title: string; right?: string; children: ReactNode; note?: ReactNode }) {
  const co = useQuery({ queryKey: ['acc-books', 'trade-setup'], queryFn: tradeSetup, staleTime: 600_000 });
  return (
    <div>
      <div className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)]">
        <div className="grid grid-cols-[1fr_auto] items-center gap-2 border-b border-[var(--border-color)] bg-accent/10 px-3 py-2 text-xs sm:grid-cols-3">
          <span className="font-extrabold text-[var(--text-primary)]">{title}</span>
          <span className="hidden text-center font-semibold text-[var(--text-secondary)] sm:block">{co.data?.company}</span>
          <span className="text-right text-[var(--text-muted)]">{right}</span>
        </div>
        <div className="overflow-x-auto">{children}</div>
      </div>
      <p className="mt-2 text-[11px] text-[var(--text-muted)] print:hidden">{note ?? 'Click a line to open it — or use ↑ ↓ and Enter, Esc to go back.'}</p>
    </div>
  );
}

export function Loading({ q, children }: { q: { isLoading: boolean; isError: boolean; error: unknown }; children: ReactNode }) {
  if (q.isLoading) return <p className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</p>;
  if (q.isError) return <p className="py-8 text-center text-sm text-danger">{apiError(q.error, 'Could not load the report')}</p>;
  return <>{children}</>;
}

/** Ledger Vouchers — one ledger's entries with a running balance; a voucher opens on click. */
export function LedgerVouchersView({ ledgerId, from, to, onBack }: { ledgerId: number; from: string; to: string; onBack?: () => void }) {
  const q = useQuery({ queryKey: ['acc-books', 'ledger-vouchers', ledgerId, from, to], queryFn: () => getLedgerVouchers(ledgerId, { from, to }) });
  const [view, setView] = useState<number | null>(null);
  const d = q.data;
  const acts = useMemo(() => [null, ...(d?.rows ?? []).map((r) => () => setView(r.voucherId)), null], [d]);
  const { row } = useKeyNav(acts, onBack);
  const excel = useExcel();
  return (
    <>
      <PeriodBar onBack={onBack} onExcel={d ? () => excel(`Ledger_${d.ledger.name}`, 'Ledger Vouchers', ['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Debit', 'Credit', 'Balance'],
        [['', 'Opening Balance', '', '', null, null, d.opening], ...d.rows.map((r) => [r.date, r.particulars, r.type, r.voucherNo, r.debit || null, r.credit || null, r.balance] as ExportCell[]), ['', 'Closing Balance', '', '', null, null, d.closing]]) : undefined} />
      <Loading q={q}>
        {d && (
          <ReportFrame title={`Ledger: ${d.ledger.name}`} right={`${tdate(from)} to ${tdate(to)}`} note="Click a voucher to open it — or use ↑ ↓ and Enter, Esc to go back.">
            <table className="w-full min-w-[640px] text-xs">
              <thead>
                <tr className="border-b border-[var(--border-color)] text-left">
                  <th className={th}>Date</th><th className={th}>Particulars</th><th className={th}>Vch Type</th><th className={th}>Vch No.</th>
                  <th className={`${th} text-right`}>Debit</th><th className={`${th} text-right`}>Credit</th><th className={`${th} text-right`}>Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr {...row(0, 'font-semibold')}><td className={td} /><td className={td} colSpan={5}>Opening Balance</td><td className={num}>{drcr(d.opening)}</td></tr>
                {d.rows.map((r, i) => (
                  <tr key={`${r.voucherId}-${i}`} {...row(i + 1)}>
                    <td className={`${td} whitespace-nowrap`}>{tdate(r.date)}</td>
                    <td className={td}>{r.particulars}{r.narration && <div className="text-[11px] text-[var(--text-muted)]">{r.narration}</div>}</td>
                    <td className={td}>{r.type}</td>
                    <td className={`${td} whitespace-nowrap font-mono`}>{r.voucherNo}</td>
                    <td className={num}>{amt(r.debit)}</td><td className={num}>{amt(r.credit)}</td><td className={num}>{drcr(r.balance)}</td>
                  </tr>
                ))}
                <tr {...row(d.rows.length + 1, 'font-bold')}><td className={td} /><td className={td} colSpan={5}>Closing Balance</td><td className={num}>{drcr(d.closing)}</td></tr>
              </tbody>
            </table>
          </ReportFrame>
        )}
      </Loading>
      {view != null && <VoucherView id={view} onClose={() => setView(null)} />}
    </>
  );
}
